import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'

/**
 * MarkdownPreview - Compact inline markdown preview (truncated, no formatting)
 *
 * **USE WHEN:** You need a compact inline preview of markdown content
 *
 * **LIBRARY:** react-markdown (lightweight, minimal rendering)
 *
 * **FEATURES:**
 * - Automatic truncation (default 200 chars)
 * - Flattened formatting (no block elements)
 * - Headings → bold text
 * - Lists → inline with bullets
 * - No images, no tables
 * - Perfect for list views and cards
 *
 * **EXAMPLES:**
 * - FAQ list previews (faq-list.tsx)
 * - Search result snippets
 * - Card content previews
 *
 * **DON'T USE FOR:**
 * - Full content display → use MarkdownRenderer instead
 * - Editing → use MarkdownEditor instead
 */
interface MarkdownPreviewProps {
  content: string
  maxLength?: number
  className?: string
}

export function MarkdownPreview({ content, maxLength = 200, className }: MarkdownPreviewProps) {
  // Truncate content if needed
  const truncated = content.length > maxLength
    ? content.substring(0, maxLength) + '...'
    : content

  return (
    <div className={cn('prose prose-sm prose-neutral dark:prose-invert max-w-none', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Compact styling for preview
          h1: ({ children }) => <p className="font-bold">{children}</p>,
          h2: ({ children }) => <p className="font-bold">{children}</p>,
          h3: ({ children }) => <p className="font-semibold">{children}</p>,
          h4: ({ children }) => <p className="font-semibold">{children}</p>,
          h5: ({ children }) => <p className="font-semibold">{children}</p>,
          h6: ({ children }) => <p className="font-semibold">{children}</p>,

          // Inline styles
          p: ({ children }) => <span>{children} </span>,

          // Lists as inline
          ul: ({ children }) => <span>{children}</span>,
          ol: ({ children }) => <span>{children}</span>,
          li: ({ children }) => <span>• {children} </span>,

          // Code inline
          code: ({ children }) => (
            <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">
              {children}
            </code>
          ),
          pre: ({ children }) => <span>{children}</span>,

          // Links
          a: ({ href, children }) => (
            <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),

          // Hide images in preview
          img: () => null,

          // Inline blockquotes
          blockquote: ({ children }) => (
            <span className="italic text-muted-foreground">{children}</span>
          ),

          // Remove hr
          hr: () => null,

          // Emphasis
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
        }}
      >
        {truncated}
      </ReactMarkdown>
    </div>
  )
}